import { getRequest, postRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type FloorPlan from '../models/floor-plan';

export type FloorPlanUploadResult = 'saved' | 'duplicateStorey' | 'invalidStorey' | 'failed';

/**
 * Uploads a floorplan's layers for one storey.
 *
 * Returns whether it was **saved**, which is the opposite of what this used to return: it
 * answered `error !== undefined`, and the one caller read that as success. The result was
 * that a successful upload showed "something went wrong" and left the dialog open, while a
 * rejected one closed it and said the floorplan had been uploaded. Named results now, so the
 * next reader cannot get the polarity wrong by squinting.
 */
const saveFloorPlanForOffice = async (
  floorPlan: FloorPlan,
  keycloak: KeycloakContext,
  /** Units per metre, when the source knew — a DXF that declares millimetres knows exactly. */
  unitsPerMetre: number | null = null,
): Promise<FloorPlanUploadResult> => {
  const apiResponse = await postRequest<boolean>('floorplan', keycloak, {
    OfficeId: floorPlan.officeId,
    Storey: floorPlan.storey,
    BorderLayer: JSON.stringify(floorPlan.borderLayer),
    WorkplacesLayer: JSON.stringify(floorPlan.workplacesLayer),
    RoomLayer: JSON.stringify(floorPlan.roomLayer),
    FloorLayer: JSON.stringify(floorPlan.floorLayer),
    // Sent only when there is one: the column is nullable, and sending the string "undefined"
    // would store a layer the app then fails to parse.
    LabelLayer: floorPlan.labelLayer ? JSON.stringify(floorPlan.labelLayer) : undefined,
    // Omitted rather than sent as null: the server leaves a scale alone when it is absent,
    // and sending null from every upload is how a measurement gets erased.
    UnitsPerMetre: unitsPerMetre && unitsPerMetre > 0 ? unitsPerMetre : undefined,
  });

  if (apiResponse.status === 409) {
    // This office already has a floorplan for this storey. Worth its own answer: the fix is
    // to edit that floorplan or pick another storey, not to try the upload again.
    return 'duplicateStorey';
  }

  if (apiResponse.status === 400 && (apiResponse.errorBody as { error?: string } | undefined)?.error === 'invalid-storey') {
    return 'invalidStorey';
  }

  return apiResponse.error === undefined ? 'saved' : 'failed';
};

/**
 * The floors an office already has a plan for, so a new one can be refused a number that is
 * taken before anything is uploaded. Undefined when they could not be read — the server still
 * refuses a taken floor then.
 */
export const getFloorPlanStoreys = async (officeId: string, keycloak: KeycloakContext): Promise<number[] | undefined> => {
  // Unpublished plans hold their floor too.
  const apiResponse = await getRequest<Array<{ storey?: number }>>(`floorplan?officeId=${officeId}&includeUnpublished=true`, keycloak);

  if (apiResponse.error || !apiResponse.json) {
    return undefined;
  }

  return apiResponse.json.map((floorPlan) => floorPlan.storey ?? 0);
};

export default saveFloorPlanForOffice;
